import { useEffect, useState } from "react";

import axios from "axios";
import { useForm } from "react-hook-form";

import { useUser } from "@components/user-context";
import { useLogger } from "@components/v2/analytics";
import { PrimaryButton, SecondaryButton } from "@shared/buttons";
import { FileInput } from "@shared/file-input";
import { Modal } from "@shared/modal";
import { TextInput } from "@shared/text-input";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  readonly onUploadSuccess: () => void;
}

export const UploadDocumentModal = ({
  isOpen,
  onClose,
  onUploadSuccess,
}: Props) => {
  const logger = useLogger({});
  const { user } = useUser();

  const form = useForm();

  const [loading, setLoading] = useState(false);
  const [feedback, setFeedback] = useState("");

  useEffect(() => {
    setFeedback("");
    form.reset();
    setLoading(false);
  }, [isOpen]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      titleContent={<div className="font-bold text-xl">Upload Document</div>}
    >
      <form
        onSubmit={form.handleSubmit(async (formData) => {
          setLoading(true);
          try {
            const response = await user.callApi<{
              status: string;
              signedUrl: string;
            }>("/api/uploads/signed-url", {
              params: { fileName: formData.name },
            });

            await axios.put(response.signedUrl, formData.file[0], {
              headers: {
                "Content-Type":
                  formData.file[0].type || "application/octet-stream",
              },
            });
          } catch (error: unknown) {
            setFeedback("There was an error submitting the document.");
            logger.error(
              "There was an error submitting a document.",
              { error, user },
              { notify: true },
            );
            return;
          } finally {
            setLoading(false);
          }
          form.reset();
          onUploadSuccess();
        })}
      >
        <div className="flex flex-col gap-4">
          <TextInput
            label="NAME"
            errorMessage={form.formState.errors?.name?.message as string}
            {...form.register("name", {
              required:
                "Please provide a file name that is at least 5 characters long.",
              minLength: {
                value: 5,
                message:
                  "Please provide a file name that is at least 5 characters long.",
              },
            })}
          />
          <FileInput
            label="FILE"
            errorMessage={form.formState.errors?.file?.message as string}
            {...form.register("file", {
              required: "Please select a file to upload.",
            })}
          />
          {feedback && <div className="text-mars+1 text-xs">{feedback}</div>}
          <div className="flex items-center justify-end gap-2">
            <SecondaryButton onClick={() => onClose()}>CANCEL</SecondaryButton>
            <PrimaryButton loading={loading} type="submit">
              UPLOAD
            </PrimaryButton>
          </div>
        </div>
      </form>
    </Modal>
  );
};
